
    import {Inject, Options, Ref, Vue} from 'vue-property-decorator';
    import LocalisationSide from '@/profile-creation-wizard/components/for-creation/ui/4_localisation/LocalisationSide.vue';
    import LocationSelection from '@/profile-creation-wizard/components/for-creation/ui/4_localisation/LocationSelection.vue';
    import PhoneSelection from '@/profile-creation-wizard/components/for-creation/ui/4_localisation/PhoneSelection.vue';
    import LocationAndWorkplace from '@/profile-creation-wizard/components/for-creation/ui/4_localisation/location/LocationAndWorkplace.vue';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {CreationFormValidation, HostConfiguration} from '@/profile-creation-wizard/components/for-creation/ProfileCreationForm';
    import mutations from '@/profile-creation-wizard/components/for-creation/store/mutations';
    import {pushNotification} from '@malt/joy-entrypoint/src/js/notification';
    import StepActions from '@/profile-creation-wizard/common/ui/step/StepActions.vue';
    import StepMobileHeader from '@/profile-creation-wizard/common/ui/step/StepMobileHeader.vue';
    import {ProfileCreationService} from '@/profile-creation-wizard/components/for-creation/ProfileCreationService';
    import getters from '@/profile-creation-wizard/components/for-creation/store/getters';
    import {ProfileCreationForm} from '@/profile-creation-wizard/components/for-creation/store';
    import {Getter} from 'vuex-class';
    import LanguageSelection from '@/profile-creation-wizard/components/for-creation/ui/4_localisation/LanguageSelection.vue';
    import {TrackingRepository} from '@/common/repositories/track/TrackingRepository';
    import type {InputPhoneValidation} from '@/profile-creation-wizard/common/types';
    import {StepNavigator} from '@/profile-creation-wizard/common/step/StepNavigator';
    import {ProfileCreationFunnelMode} from '@/profile-creation-wizard/config/steps/ProfileCreationSteps';

    @Options({
        components: {
            LocalisationSide,
            LocationSelection,
            LocationAndWorkplace,
            LanguageSelection,
            StepActions,
            StepMobileHeader,
            PhoneSelection,
        },
    })
    export default class LocalisationStep extends Vue {
        @Inject({from: DI.creationFormValidation})
        creationFormValidation!: CreationFormValidation;
        @Inject({from: DI.profileCreationService})
        profileCreationService!: ProfileCreationService;
        @Inject({from: DI.trackingRepository})
        trackingRepository!: TrackingRepository;
        @Getter(getters.creationForm)
        creationForm!: ProfileCreationForm;
        @Ref('locationAndWorkplace')
        locationAndWorkplace!: LocationAndWorkplace;
        @Ref('refPhoneSelection')
        refPhoneSelection!: typeof PhoneSelection;

        stepName = 'localisation';
        isBlockedCountry = false;
        progressPercentage = 0;
        loading: boolean = false;
        phone: number | null = null;
        phoneValidation: InputPhoneValidation | null = null;
        displayPhoneValidation: boolean = false;
        mode: ProfileCreationFunnelMode = ProfileCreationFunnelMode.FREELANCER;

        created(): void {
            this.trackingRepository.track('localisation-view-page-v2');
            window.Hop.events.on('user:connected', this.submit);

            const meta = this.$router.currentRoute.value.meta;
            this.mode = meta.funnelMode;
            this.progressPercentage = meta.progressPercentage || 0;
            this.stepNavigator = new StepNavigator(this.$router, meta.previousStep, meta.nextStep);
        }

        isValidPhoneSelection(): boolean {
            return this.$refs.refPhoneSelection.isValidPhoneNumber();
        }

        beforeUnmount(): void {
            window.Hop.events.off('user:connected', this.submit);
        }

        get hostConfiguration(): HostConfiguration {
            return {
                priceConfig: {
                    maxPriceAllowed: this.$pageContext.mandatoryDetails.maxPriceAllowed,
                    minPriceAllowed: this.$pageContext.mandatoryDetails.minPriceAllowed,
                },
            };
        }

        get isPhoneValid(): boolean {
            return (this.phoneValidation?.validated && !this.phoneValidation.isInitialising) || false;
        }

        goToPrevious(): void {
            this.stepNavigator.previous();
        }

        goNext(): void {
            if (this.phone) {
                if (!this.isPhoneValid) {
                    this.displayPhoneValidation = true;
                    return;
                } else {
                    this.$store.commit(mutations.selectPhone, this.phone);
                }
            }

            this.trackingRepository.track('location-chosen-v2', {
                location: this.creationForm.address,
            });

            this.trackingRepository.track('languages-chosen-v2', {
                languages: this.creationForm.languages.map((language) => ({
                    language: language.query,
                    code: language.code,
                    level: language.level,
                })),
            });
            this.askForSignupAccount();
        }

        get isCompletelyValid(): boolean {
            if (this.loading || this.disableForm) {
                return false;
            }
            // TODO move to creationFormValidation.isValid when you remove this FF

            const isWorkplacePreferenceValid = this.isActivatedNewLocationAndWorkplace
                ? this.creationFormValidation.isWorkplacePreferenceValid(this.creationForm.workPlacePreference)
                : true;
            return (
                this.creationFormValidation.isValid(this.creationForm, this.hostConfiguration, this.$router.currentRoute.value.meta.funnelMode) &&
                isWorkplacePreferenceValid
            );
        }

        checkCountryValidity(): void {
            this.isBlockedCountry = this.locationAndWorkplace.isBlockedCountry;
        }

        get isActivatedNewLocationAndWorkplace(): boolean {
            return this.$features.profileCreationLocationAndWorkplace;
        }

        get disableForm(): boolean {
            return this.$features.profileCreationLocationAndWorkplace && this.isBlockedCountry;
        }

        async askForSignupAccount() {
            if (!this.$pageContext.isConnected) {
                const triggerSignupLink = document.getElementById('spanSignupModal')!;
                triggerSignupLink.click();

                return;
            }

            await this.submit();
        }

        nextLocationHref() {
            return this.stepNavigator.nextHref();
        }

        async submit(): Promise<void> {
            this.loading = true;
            if (localStorage.getItem('mfi-code')) {
                this.$store.commit(mutations.selectInvitationCode, localStorage.getItem('mfi-code'));
                localStorage.removeItem('mfi-code');
            }

            let groupInvitation = this.$pageContext.groupInvitation;
            if (!groupInvitation) {
                groupInvitation = localStorage.getItem('grp-code');
            }

            if (groupInvitation) {
                this.$store.commit(mutations.selectGroupInvitationId, groupInvitation);
                localStorage.removeItem('grp-code');
            }

            const [response, error] = await this.profileCreationService.create(this.creationForm, this.mode);

            if (error) {
                if (error.response?.status === 401) {
                    pushNotification(error.response.data, {level: 'error'});
                }

                if (error.response?.status === 400) {
                    error.response.data.forEach((errorItem: any) => {
                        pushNotification(errorItem.error, {level: 'error'});
                    });
                }
                this.loading = false;
                return;
            }

            if (response) {
                location.href = this.nextLocationHref();
            }
        }
    }
